/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useContext, Dispatch } from 'react';
import { Auth } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import {
  SocialLoginMessage,
  SocialLoginPlatform,
} from '@website/components/auth/utils/SocialLoginButtons';
import { Store } from '../../Store';
import LoadingPage from '../utilities/LoadingPage';

export enum CognitoHostedUIIdentityProvider {
  Cognito = 'COGNITO',
  Google = 'Google',
  Facebook = 'Facebook',
  Amazon = 'LoginWithAmazon',
  Apple = 'SignInWithApple',
}

/**
 * Send a message to the tab that opened this popup
 */
export function sendMessageToOpener(success: boolean, errorMessage?: string) {
  if (window.opener) {
    const socialLoginMessage: SocialLoginMessage = {
      source: 'skyhook-social-login',
      success,
      errorMessage,
    };
    window.opener.postMessage(socialLoginMessage, window.location.origin);
  }
}

/**
 * Handle "Account already exists" errors
 *
 * This occurs when the Cognito lambda trigger has not finished merging the accounts before Cognito responds. We solve
 * by simply signing in again with the same social provider (this time it won't ask for credentials again and will
 * instead instantly approve).
 */
export async function handleAccountAlreadyExistsError() {
  let provider: SocialLoginPlatform;

  try {
    // Provider is set in @link SocialLoginButtons
    provider = localStorage.getItem(
      'socialLoginPlatform',
    ) as SocialLoginPlatform;
  } catch (_e) {
    // When localStorage is not available it will error out (as a security issue)
    // In this case continue instead without trying to sign in once more
    return;
  }

  const providerMapping = {
    [SocialLoginPlatform.GOOGLE]: CognitoHostedUIIdentityProvider.Google,
    [SocialLoginPlatform.FACEBOOK]: CognitoHostedUIIdentityProvider.Facebook,
  };

  await Auth.federatedSignIn({ provider: providerMapping[provider] });
}

/**
 * Hub auth event handler
 *
 * Listens to auth events (e.g. `signIn`) and acts on them, e.g. by notifying the main tab of the new user state and
 * closing this popup.
 */
export async function hubAuthEventHandler(event: any, dispatch: Dispatch<any>) {
  switch (event.payload.event) {
    case 'signUp':
    case 'signIn':
      sendMessageToOpener(true);
      break;
    case 'signOut':
      dispatch({ type: 'SET_SIGNED_OUT' });
      break;
    case 'signIn_failure':
      // Get the error message
      // eslint-disable-next-line no-case-declarations
      const message = JSON.stringify(event.payload.data.message);

      // Handle the Cognito bug "Already found..." by signing in once more
      // If this succeeds it will also terminate this function (as it opens the social provider website)
      if (message.includes('Already+found+an+entry+for+username')) {
        await handleAccountAlreadyExistsError();
      }

      // Log the bug if not handled above
      // eslint-disable-next-line no-case-declarations
      let provider = '';
      try {
        provider = localStorage.getItem('socialLoginPlatform');
        // eslint-disable-next-line no-empty
      } catch (_e) {}
      // eslint-disable-next-line no-console
      console.error(
        JSON.stringify({
          error: 'Social sign in failed',
          payload: event.payload,
          provider,
        }),
      );

      // Send the error message back to the opener window
      sendMessageToOpener(false, event.payload.message);
      break;
    default:
      // There can be other events (e.g. configure) that we should simply ignore
      break;
  }

  // Close the window as we're done here
  // Timeout so that the message, and amplify cache, have enough time to update
  setTimeout(() => window.close(), 800);
}

/**
 * Social Login Callback handler
 *
 * For use on the `/auth/callback` page only to handle the social login response in this popup, and return control to
 * the main window with the success/failed state.
 */
export default function SocialLoginCallback() {
  const { dispatch } = useContext(Store);

  useEffect(() => {
    // Setup the Hub listener to respond to social login auth events (e.g. "signIn")
    Hub.listen('auth', (event) => hubAuthEventHandler(event, dispatch));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show a loading page until done (and this popup/tab is closed)
  return <LoadingPage />;
}
